/**
 * TimeEntryCard Component
 *
 * Displays a single time entry with check-in/check-out times,
 * duration, and late entry indicator.
 *
 * @module pages/technician/time/TimeEntryCard
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Badge } from 'react-bootstrap';
import './TimeEntryPage.css';

const formatTime = (isoString) => {
  if (!isoString) return '--:--';
  return new Date(isoString).toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatDuration = (hours) => {
  if (!hours || hours === 0) return '--';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const TimeEntryCard = ({ entry, onClick, showProject = true }) => {
  return (
    <div
      className="time-entry-card"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="time-entry-dot">
        <i className="bi bi-circle-fill"></i>
      </div>

      <div className="time-entry-content">
        <div className="time-entry-header">
          {showProject && entry.project && (
            <span className="time-entry-project">
              {entry.project.project_number || entry.project.title}
            </span>
          )}
          {entry.total_hours && (
            <Badge bg="primary" className="time-entry-duration-badge">
              {formatDuration(Number(entry.total_hours))}
            </Badge>
          )}
        </div>

        {entry.task && (
          <div className="time-entry-task">
            <i className="bi bi-list-task me-1"></i>
            {entry.task.title}
          </div>
        )}

        <div className="time-entry-times">
          <div className="time-entry-time">
            <i className="bi bi-box-arrow-in-right text-success me-1"></i>
            <span className="time-label">Entrada:</span>
            <span className="time-value">{formatTime(entry.check_in_time)}</span>
          </div>

          {entry.check_out_time && (
            <div className="time-entry-time">
              <i className="bi bi-box-arrow-right text-danger me-1"></i>
              <span className="time-label">Salida:</span>
              <span className="time-value">{formatTime(entry.check_out_time)}</span>
            </div>
          )}
        </div>

        {entry.is_late_entry && (
          <div className="time-entry-late-badge">
            <Badge bg="warning" text="dark">
              <i className="bi bi-clock-history me-1"></i>
              Registro tardio
            </Badge>
          </div>
        )}

        {entry.notes && (
          <div className="time-entry-notes">
            <i className="bi bi-chat-text me-1"></i>
            <span>{entry.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
};

TimeEntryCard.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string.isRequired,
    check_in_time: PropTypes.string.isRequired,
    check_out_time: PropTypes.string,
    total_hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    is_late_entry: PropTypes.bool,
    notes: PropTypes.string,
    project: PropTypes.shape({
      project_number: PropTypes.string,
      title: PropTypes.string,
    }),
    task: PropTypes.shape({
      title: PropTypes.string,
    }),
  }).isRequired,
  onClick: PropTypes.func,
  showProject: PropTypes.bool,
};

export default TimeEntryCard;
